<template>
  <AutoCompleteShell
    ref="autocomplete"
    label="Followers"
    name="Followers"
    variable-height
    :disabled="isDisabled"
    :error-messages="remoteErrors"
    @close="onClose"
    @close-expand="clearSearch"
    @clear="clear"
    @update:search="updateSearch"
  >
    <template #popover>
      <picker-list-virtualized ref="virtualizedList" :items="items">
        <template #item="{ item }">
          <picker-list-item
            :item="item"
            :is-selected="isSelected(item)"
            checkbox
            checkbox-color="var(--color-blue)"
            dense
            @select="customSelectItem"
          />
        </template>
      </picker-list-virtualized>
      <div v-if="noResultsMessage" class="no-data">
        <v-container class="my-4">
          <v-row justify="center">
            <vs-text center type="subdued">{{ noResultsMessage }}</vs-text>
          </v-row>
        </v-container>
      </div>
    </template>
  </AutoCompleteShell>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, toRef } from 'vue'
import { useJobFollowersPicker } from '@/components/tickets/JobFollowersPicker/JobFollowersPicker'
import {
  HeadlessAutoCompleteValueType,
  ValueResolver,
} from '@/components/vision/next/inputs/useAutoComplete'
import User from '@/models/user'
import { useStore } from 'vuex-composition-helpers'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import VsText from '@/components/vision/VsText.vue'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'
import { PickerItemType } from '@/components/vision/next/picker-list/models'

const props = defineProps<{
  followerIds: string[]
  /** The loaded job's sid for each follower id, to name an id the users list doesn't know */
  followerSidsById?: Record<string, string>
  isDisabled: boolean
  jobHubId: string
}>()
const emit = defineEmits<{
  (e: 'selected', id: any): void
}>()

const store = useStore()
const allUsers = computed<Record<string, User>>(() => store.getters['users/getAllUsers'])
const getUserBySid = computed<(sid: string) => User | null>(() => store.getters['users/getBySid'])

const followerIdsAsRef = toRef(props, 'followerIds')
const jobHubIdAsRef = toRef(props, 'jobHubId')
const autocomplete = ref()
const virtualizedList = ref()
const onNavigate = () => {
  autocomplete.value?.input?.focus()
}

const scrollToPosition = (pos: number) => {
  virtualizedList?.value?.scrollToPosition(pos)
}

const onValueChanged = (items: HeadlessAutoCompleteValueType[]) => {
  emit('selected', items?.map((i) => i.id as string) ?? [])
}

const isSelected = (item: HeadlessAutoCompleteValueType) => {
  return followerIdsAsRef.value.includes(item.id as string)
}

const valueResolver = async (id: string | null) => {
  const user = id ? allUsers.value[id] : null
  if (user) return { id: user.uuid, title: user.name }
  if (!id) return { id: null, title: null }

  // Keep an id the users list doesn't know (e.g. a hidden multi-org record): resolving it to null
  // would emit it back as a changed selection and drop the follower on save. Its visible records
  // share its sid and name.
  const sid = props.followerSidsById?.[id]
  return { id, title: (sid && getUserBySid.value(sid)?.name) || 'Unknown User' }
}

const onClose = () => {
  nextTick().then(() => runValidation())
}

const { allErrorMessages: remoteErrors, runValidation } = useFormField({
  value: followerIdsAsRef,
  required: false,
})

const customSelectItem = (arg: PickerItemType) => {
  autocomplete.value.input.focus()
  selectItem(arg)
  clearSearch()
}

const { items, updateSearch, selectItem, clear, clearSearch, noResultsMessage } =
  useJobFollowersPicker({
    onNavigate,
    scrollToPosition,
    valueResolver: valueResolver as ValueResolver,
    onValueChanged,
    value: followerIdsAsRef,
    jobHubId: jobHubIdAsRef,
  })
</script>

<style scoped></style>
